import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Award, Skull, RotateCcw, BookImage } from "lucide-react";
import { clearStoredSessionId, getStoredSessionId, listPhotos } from "../game/api";

export default function EndScreen({ tts, ttsEnabled }) {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const outcome = params.get("outcome") === "win" ? "win" : "lose";
    const [photoCount, setPhotoCount] = useState(null);

    useEffect(() => {
        const id = getStoredSessionId();
        if (id) listPhotos(id).then(ph => setPhotoCount(ph.length)).catch(() => setPhotoCount(0));
    }, []);

    useEffect(() => {
        if (ttsEnabled && tts?.speak) {
            tts.speak(outcome === "win"
                ? "You escaped the darkroom. The Phantom slinks back into the shadows."
                : "The Phantom has you. The negatives have been confiscated.");
        }
    }, [outcome, ttsEnabled, tts]);

    const restart = () => {
        clearStoredSessionId();
        nav("/");
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-6 py-12" data-testid="end-screen">
            <div className="max-w-2xl w-full border-2 border-amber-30 bg-bg2/60 p-8 sm:p-12 relative grain">
                <div className="text-[10px] tracking-[0.35em] uppercase text-amber mb-4 flex items-center gap-2">
                    {outcome === "win" ? <Award size={14} /> : <Skull size={14} />}
                    {outcome === "win" ? "Roll Developed · Escape Confirmed" : "Negative Burned · You Did Not Survive"}
                </div>
                <h1
                    className="font-display uppercase tracking-tighter font-bold text-shadow-glow text-amber leading-[0.95] text-[clamp(2.5rem,7vw,5rem)]"
                    data-testid="end-title"
                >
                    {outcome === "win" ? "You Escape" : "Phantom Wins"}
                </h1>
                <p className="mt-4 text-[var(--text-secondary)] font-mono text-sm" data-testid="end-blurb">
                    {outcome === "win"
                        ? "Dawn light leaks under the basement door. Twenty-four perfectly exposed frames. The yearbook is saved."
                        : "He tears the last print clean in half. You'll be added to his collection of failed students. Class of 2026 will remember nothing."}
                </p>

                <div className="mt-8 grid grid-cols-2 gap-4 text-xs">
                    <div className="border border-amber-30 p-3">
                        <div className="text-[10px] tracking-[0.3em] text-amber uppercase">Negatives Captured</div>
                        <div className="font-display text-2xl mt-1" data-testid="end-photo-count">{photoCount === null ? "…" : photoCount}</div>
                    </div>
                    <div className="border border-amber-30 p-3">
                        <div className="text-[10px] tracking-[0.3em] text-amber uppercase">Result</div>
                        <div className="font-display text-2xl mt-1">
                            {outcome === "win" ? "ESCAPED" : "LOST"}
                        </div>
                    </div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    <button onClick={restart} className="dial-btn selected px-6 py-3 flex items-center gap-2 justify-center" data-testid="end-restart">
                        <RotateCcw size={14} /> Reload the Camera
                    </button>
                    <button onClick={() => nav("/roll")} className="dial-btn px-6 py-3 flex items-center gap-2 justify-center" data-testid="end-view-roll">
                        <BookImage size={14} /> View Camera Roll
                    </button>
                </div>
            </div>
        </div>
    );
}
